import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { configuracionAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { useBranding } from '../context/BrandingContext';
import { resolveLogoUrl, ENCABEZADO_IMPRESION_DEFAULT, normalizarEncabezadoImpresion } from '../utils/printBranding';
import { NOMBRES_DIA } from '../utils/horariosUso';
import {
  etiquetaZona,
  etiquetaFormatoFecha,
  formatFechaSistema,
  relojSistema,
  FORMATO_FECHA_DEFAULT,
} from '../utils/fechasSistema';
import '../Configuracion.css';

const horariosVacios = () =>
  NOMBRES_DIA.map((_, dia) => ({
    dia,
    activo: dia >= 1 && dia <= 5,
    desde: '08:00',
    hasta: dia === 6 ? '13:00' : '18:00',
  }));

const Configuracion = () => {
  const { hasPermission } = useAuth();
  const { refresh: refreshZonaGlobal } = useZonaHoraria();
  const { logoUrl, refresh: refreshBranding } = useBranding();
  const puedeEditar = hasPermission('gestionar_usuarios');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [zonaHoraria, setZonaHoraria] = useState('America/Argentina/Buenos_Aires');
  const [formatoFecha, setFormatoFecha] = useState(FORMATO_FECHA_DEFAULT);
  const [relojPreview, setRelojPreview] = useState('');
  const [usarHorarios, setUsarHorarios] = useState(false);
  const [horarios, setHorarios] = useState(horariosVacios);
  const [zonasDisponibles, setZonasDisponibles] = useState([]);
  const [mostrarRestriccionHorarios, setMostrarRestriccionHorarios] = useState(false);
  const [encabezadoImpresion, setEncabezadoImpresion] = useState(ENCABEZADO_IMPRESION_DEFAULT);
  const [fiscal, setFiscal] = useState({
    condicion_emisor: '',
    ingresos_brutos: '',
    inicio_actividades: '',
    punto_venta: {
      numero: '',
      denominacion: '',
      calle: '',
      piso_depto: '',
      localidad: '',
      codigo_postal: '',
      provincia: '',
    },
  });

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    const tick = () => setRelojPreview(relojSistema(zonaHoraria));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [zonaHoraria]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await configuracionAPI.getSistema();
      if (res.success && res.data) {
        setZonaHoraria(res.data.zona_horaria || 'America/Argentina/Buenos_Aires');
        setFormatoFecha(res.data.formato_fecha || FORMATO_FECHA_DEFAULT);
        setUsarHorarios(!!res.data.usar_horarios);
        setHorarios(Array.isArray(res.data.horarios) ? res.data.horarios : horariosVacios());
        setMostrarRestriccionHorarios(!!res.data.usar_horarios);
        setEncabezadoImpresion(normalizarEncabezadoImpresion(res.data.encabezado_impresion));
        if (res.data.fiscal) {
          const pv = res.data.fiscal.punto_venta || {};
          setFiscal({
            condicion_emisor: res.data.fiscal.condicion_emisor || '',
            ingresos_brutos: res.data.fiscal.ingresos_brutos || '',
            inicio_actividades: res.data.fiscal.inicio_actividades || '',
            punto_venta: {
              numero: pv.numero ?? '',
              denominacion: pv.denominacion || '',
              calle: pv.calle || '',
              piso_depto: pv.piso_depto || '',
              localidad: pv.localidad || '',
              codigo_postal: pv.codigo_postal || '',
              provincia: pv.provincia || '',
            },
          });
        }
      }
      if (Array.isArray(res.zonas_disponibles)) {
        setZonasDisponibles(res.zonas_disponibles);
      }
      if (res.data?.logo_url) {
        setLogoPreview(resolveLogoUrl(res.data.logo_url));
      } else {
        setLogoPreview(null);
      }
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const updateHorario = (dia, field, value) => {
    setHorarios((prev) =>
      prev.map((h) => (h.dia === dia ? { ...h, [field]: value } : h))
    );
  };

  const updateEncabezadoCampo = (campo, valor) => {
    setEncabezadoImpresion((prev) => ({ ...prev, [campo]: valor }));
  };

  const toggleEncabezadoIncluir = (campo) => {
    setEncabezadoImpresion((prev) => ({
      ...prev,
      incluir: { ...prev.incluir, [campo]: !prev.incluir[campo] },
    }));
  };

  const CAMPOS_ENCABEZADO = [
    { id: 'razon_social', label: 'Razón social / nombre', type: 'text', ancho: '100%' },
    { id: 'cuit', label: 'CUIT', type: 'text', ancho: '220px' },
    { id: 'condicion_iva', label: 'Condición IVA', type: 'text', ancho: '280px' },
    { id: 'direccion', label: 'Dirección', type: 'text', ancho: '100%' },
    { id: 'localidad', label: 'Localidad / CP', type: 'text', ancho: '280px' },
    { id: 'telefono', label: 'Teléfono', type: 'text', ancho: '220px' },
    { id: 'email', label: 'Email', type: 'email', ancho: '280px' },
  ];

  const handleSave = async (e) => {
    e.preventDefault();
    if (!puedeEditar) {
      toast.error('No tiene permiso para modificar la configuración');
      return;
    }
    try {
      setSaving(true);
      const res = await configuracionAPI.saveSistema({
        zona_horaria: zonaHoraria,
        formato_fecha: formatoFecha,
        usar_horarios: usarHorarios,
        horarios,
        encabezado_impresion: encabezadoImpresion,
        fiscal,
      });
      if (res.success) {
        toast.success(res.message || 'Configuración guardada.');
        await refreshZonaGlobal();
        await refreshBranding();
      } else {
        toast.error(res.message || 'Error al guardar');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !puedeEditar) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Formato no permitido. Use PNG, JPG, WEBP o SVG.');
      e.target.value = '';
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error('El logo no puede superar 512 KB.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setLogoUploading(true);
        const res = await configuracionAPI.uploadLogo(reader.result, file.type);
        if (res.success) {
          toast.success('Logo guardado');
          const url = res.data?.logo_url ? resolveLogoUrl(res.data.logo_url) : null;
          setLogoPreview(url);
          await refreshBranding();
        } else {
          toast.error(res.message || 'Error al subir logo');
        }
      } catch (error) {
        toast.error(error.message || 'Error al subir logo');
      } finally {
        setLogoUploading(false);
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEliminarLogo = async () => {
    if (!puedeEditar || !window.confirm('¿Eliminar el logo de la empresa?')) return;
    try {
      setLogoUploading(true);
      const res = await configuracionAPI.deleteLogo();
      if (res.success) {
        toast.success('Logo eliminado');
        setLogoPreview(null);
        await refreshBranding();
      }
    } catch (error) {
      toast.error(error.message || 'Error al eliminar logo');
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="configuracion-page">
      <div className="page-header">
        <h1>Configuración</h1>
        {hasPermission('admin_puesta_en_marcha') && (
          <Link to="/configuracion/puesta-en-marcha" className="btn-secondary" style={{ marginTop: 8, display: 'inline-block' }}>
            Puesta en marcha
          </Link>
        )}
      </div>

      <div className="config-panel">
        <form onSubmit={handleSave}>
          <section className="config-section">
            <h2>Zona horaria del sistema</h2>
            <p>
              Elija la zona horaria oficial del sistema. Todos los usuarios verán la misma fecha y
              hora en pantalla, informes y operaciones, sin importar desde dónde se conecten.
            </p>

            <div className="reloj-preview">
              <div className="reloj-preview-label">
                Hora del sistema ({etiquetaZona(zonaHoraria)})
              </div>
              <div className="reloj-preview-time">
                {relojPreview || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="zona-horaria" style={{ display: 'block', marginBottom: 4 }}>
                Zona horaria
              </label>
              <select
                id="zona-horaria"
                value={zonaHoraria}
                onChange={(e) => setZonaHoraria(e.target.value)}
                disabled={!puedeEditar}
                style={{ minWidth: 320, padding: '8px 10px', maxWidth: '100%' }}
              >
                {(zonasDisponibles.length ? zonasDisponibles : [zonaHoraria]).map((z) => (
                  <option key={z} value={z}>
                    {etiquetaZona(z)}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="config-section">
            <h2>Formato de fecha</h2>
            <p>
              Elija cómo se muestran las fechas en tablas, informes, impresos y mensajes.
              El valor interno y la base de datos siempre usan el formato ISO (aaaa-mm-dd).
              Los selectores nativos de fecha del navegador pueden verse según el idioma del sistema operativo.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="formato-fecha" style={{ display: 'block', marginBottom: 4 }}>
                Formato de visualización
              </label>
              <select
                id="formato-fecha"
                value={formatoFecha}
                onChange={(e) => setFormatoFecha(e.target.value)}
                disabled={!puedeEditar}
                style={{ minWidth: 320, padding: '8px 10px', maxWidth: '100%' }}
              >
                <option value="DD_MM_YYYY">dd/mm/aaaa</option>
                <option value="MM_DD_YYYY">mm/dd/aaaa</option>
              </select>
            </div>
            <p style={{ margin: 0, color: '#555' }}>
              Ejemplo: {formatFechaSistema('2024-03-15', { zona: zonaHoraria, formato: formatoFecha })}
              {' '}({etiquetaFormatoFecha(formatoFecha)})
            </p>
          </section>

          <section className="config-section">
            <h2>Logo de la empresa</h2>
            <p>
              El logo se muestra en listados impresos, informes, recibos de cobro y órdenes de pago.
              Formatos: PNG, JPG, WEBP o SVG. Tamaño máximo: 512 KB.
            </p>
            <div className="logo-config-preview">
              {logoPreview || logoUrl ? (
                <img src={logoPreview || logoUrl} alt="Logo actual" className="logo-config-img" />
              ) : (
                <div className="logo-config-placeholder">Sin logo configurado</div>
              )}
            </div>
            {puedeEditar && (
              <div className="logo-config-actions">
                <label className="btn-secondary logo-upload-label">
                  {logoUploading ? 'Subiendo…' : 'Subir logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoChange}
                    disabled={logoUploading}
                    style={{ display: 'none' }}
                  />
                </label>
                {(logoPreview || logoUrl) && (
                  <button type="button" className="btn-secondary" onClick={handleEliminarLogo} disabled={logoUploading}>
                    Eliminar logo
                  </button>
                )}
              </div>
            )}
          </section>

          <section className="config-section">
            <h2>Datos del encabezado impreso</h2>
            <p>
              Se muestran junto al logo a la izquierda en recibos, órdenes de pago, informes y listados.
              Marque qué datos incluir en cada impresión.
            </p>
            <div className="encabezado-config-grid">
              {CAMPOS_ENCABEZADO.map((campo) => (
                <div key={campo.id} className="encabezado-config-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={!!encabezadoImpresion.incluir?.[campo.id]}
                      onChange={() => toggleEncabezadoIncluir(campo.id)}
                      disabled={!puedeEditar}
                    />
                    {campo.label}
                  </label>
                  <input
                    type={campo.type}
                    value={encabezadoImpresion[campo.id] || ''}
                    onChange={(e) => updateEncabezadoCampo(campo.id, e.target.value)}
                    disabled={!puedeEditar}
                    style={{ width: campo.ancho, maxWidth: '100%', padding: '8px 10px' }}
                  />
                </div>
              ))}
            </div>
          </section>

          <details
            open={mostrarRestriccionHorarios}
            className="config-section"
            onToggle={(e) => setMostrarRestriccionHorarios(e.target.open)}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '0.75rem' }}>
              Restricción opcional de horarios de envío
            </summary>
            <p style={{ fontSize: '0.95rem' }}>
              Si lo activa, los envíos masivos por WhatsApp y correo solo se permiten en los días y
              franjas indicados (según la zona horaria del sistema).
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
              <input
                type="checkbox"
                checked={usarHorarios}
                onChange={(e) => setUsarHorarios(e.target.checked)}
                disabled={!puedeEditar}
              />
              Restringir envíos según horarios configurados
            </label>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Activo</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                </tr>
              </thead>
              <tbody>
                {horarios.map((h) => (
                  <tr key={h.dia}>
                    <td>{NOMBRES_DIA[h.dia]}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!h.activo}
                        onChange={(e) => updateHorario(h.dia, 'activo', e.target.checked)}
                        disabled={!puedeEditar}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={h.desde || '08:00'}
                        onChange={(e) => updateHorario(h.dia, 'desde', e.target.value)}
                        disabled={!puedeEditar || !h.activo}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={h.hasta || '18:00'}
                        onChange={(e) => updateHorario(h.dia, 'hasta', e.target.value)}
                        disabled={!puedeEditar || !h.activo}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <section className="config-section">
            <h2>Facturación fiscal</h2>
            <p>
              El CUIT y la razón social salen del encabezado de impresión. El punto de venta es el de factura electrónica (CAE).
            </p>
            <div className="facturacion-fechas" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
                Condición IVA del emisor
                <select
                  value={fiscal.condicion_emisor}
                  disabled={!puedeEditar}
                  onChange={(e) => setFiscal((prev) => ({ ...prev, condicion_emisor: e.target.value }))}
                  style={{ fontSize: 16, minHeight: 44 }}
                >
                  <option value="">Sin definir</option>
                  <option value="monotributista">Responsable Monotributo</option>
                  <option value="responsable_inscripto">IVA Responsable Inscripto</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
                Ingresos brutos
                <input
                  type="text"
                  value={fiscal.ingresos_brutos}
                  disabled={!puedeEditar}
                  onChange={(e) => setFiscal((prev) => ({ ...prev, ingresos_brutos: e.target.value }))}
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Inicio de actividades
                <input
                  type="date"
                  value={fiscal.inicio_actividades}
                  disabled={!puedeEditar}
                  onChange={(e) => setFiscal((prev) => ({ ...prev, inicio_actividades: e.target.value }))}
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              </label>
            </div>
            <h3>Punto de venta</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {[
                ['numero', 'Número', 'number'],
                ['denominacion', 'Denominación', 'text'],
                ['calle', 'Calle y número', 'text'],
                ['piso_depto', 'Piso y departamento', 'text'],
                ['localidad', 'Localidad', 'text'],
                ['codigo_postal', 'Código postal', 'text'],
                ['provincia', 'Provincia', 'text'],
              ].map(([campo, etiqueta, type]) => (
                <label key={campo} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
                  {etiqueta}
                  <input
                    type={type}
                    value={fiscal.punto_venta[campo] ?? ''}
                    disabled={!puedeEditar}
                    onChange={(e) => setFiscal((prev) => ({
                      ...prev,
                      punto_venta: { ...prev.punto_venta, [campo]: e.target.value },
                    }))}
                    style={{ fontSize: 16, minHeight: 44 }}
                  />
                </label>
              ))}
            </div>
            <p>Sistema de facturación: factura electrónica por Web Services (CAE).</p>
          </section>

          {puedeEditar ? (
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </button>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              Solo usuarios con permiso de administración pueden modificar estos valores.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Configuracion;
